import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { errMsg } from '../components/diagnostico/errMsg.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { ClipboardList, BarChart3, CheckCircle2, AlertCircle, Building2 } from 'lucide-react';
import Logo from '../components/Logo.js';
import DiagnosticForm from '../components/diagnostico/DiagnosticForm.js';
import DiagnosticResultsView from '../components/diagnostico/DiagnosticResultsView.js';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/diagnostics`;

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50" data-testid="public-diag-page">
    <header className="bg-slate-900 px-6 py-4"><Logo variant="dark" size="small" /></header>
    <main className="max-w-4xl mx-auto px-4 py-8 flex flex-col gap-5">{children}</main>
  </div>
);

const Notice = ({ icon: Icon, title, text, testId }) => (
  <div className="bg-white border rounded-xl p-8 text-center" data-testid={testId}>
    <Icon className="w-10 h-10 mx-auto text-slate-400 mb-3" />
    <h2 className="font-semibold text-slate-800">{title}</h2>
    <p className="text-sm text-slate-500 mt-1">{text}</p>
  </div>
);

const TabButton = ({ active, onClick, icon: Icon, label, testId }) => (
  <Button size="sm" variant={active ? 'default' : 'outline'} onClick={onClick} data-testid={testId}>
    <Icon className="w-4 h-4 mr-1" /> {label}
  </Button>
);

const DiagnosticoPublico = () => {
  const { token } = useParams();
  const [diag, setDiag] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('form');
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/${token}`)
      .then(r => { setDiag(r.data); if (r.data.can_see_results) setTab('results'); })
      .catch(err => setError(errMsg(err, 'Link inválido')));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab !== 'results' || results) return;
    axios.get(`${API}/${token}/results`).then(r => setResults(r.data))
      .catch(err => toast.error(errMsg(err, 'Erro ao carregar resultados')));
  }, [tab, results, token]);

  const submit = async (answers) => {
    setSaving(true);
    try {
      await axios.post(`${API}/${token}/responses`, { answers });
      toast.success('Respostas salvas com sucesso!');
      setDone(true);
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Erro ao enviar'));
    } finally { setSaving(false); }
  };

  if (error) return <Shell><Notice icon={AlertCircle} title="Acesso indisponível" text={error} testId="public-error" /></Shell>;
  if (!diag) return <Shell><p className="text-sm text-slate-400">Carregando...</p></Shell>;

  const inactive = diag.status !== 'active';
  const hasPrevious = (diag.my_answers || []).length > 0;

  return (
    <Shell>
      <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-xl p-5 text-white">
        <p className="text-xs opacity-70 flex items-center gap-1" data-testid="public-company-name"><Building2 className="w-3.5 h-3.5" /> {diag.company_name}</p>
        <h1 className="text-2xl font-semibold mt-1" data-testid="public-diag-title">{diag.nome}</h1>
        {diag.descricao && <p className="text-sm opacity-80 mt-1">{diag.descricao}</p>}
        {diag.answered_at && <p className="text-xs opacity-60 mt-2">Última resposta salva em {new Date(diag.answered_at).toLocaleString('pt-BR')}</p>}
      </div>

      {diag.can_see_results && (
        <div className="flex gap-2">
          <TabButton active={tab === 'results'} onClick={() => setTab('results')} icon={BarChart3} label="Resultados" testId="tab-results" />
          <TabButton active={tab === 'form'} onClick={() => setTab('form')} icon={ClipboardList} label="Minhas Respostas" testId="tab-form" />
        </div>
      )}

      {tab === 'results' && (results ? <DiagnosticResultsView data={results} isAdmin={false} /> : <p className="text-sm text-slate-400">Carregando resultados...</p>)}

      {tab === 'form' && inactive && !diag.can_see_results && (
        <Notice icon={AlertCircle} title="Diagnóstico fechado" text="Este diagnóstico não está recebendo respostas no momento." testId="public-inactive" />
      )}

      {tab === 'form' && (!inactive || diag.can_see_results) && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ClipboardList className="w-4 h-4 text-blue-600" /> Questionário</CardTitle></CardHeader>
          <CardContent>
            {done && !diag.locked && (
              <div className="mb-4 flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg p-3" data-testid="public-success">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                Respostas recebidas. Você pode voltar a este link para editá-las até a liberação dos resultados.
              </div>
            )}
            <DiagnosticForm
              key={diag.answered_at || 'new'}
              questions={diag.questions}
              initialAnswers={diag.my_answers}
              locked={diag.locked || inactive}
              hasPrevious={hasPrevious}
              saving={saving}
              onSubmit={submit}
            />
          </CardContent>
        </Card>
      )}
    </Shell>
  );
};

export default DiagnosticoPublico;
